<script setup lang="ts">
/**
 * 画像OCR NGワードチェック: 拡大表示した画像に含まれるNGワードの一覧。
 * 行の見た目は右ペインのNGワード一覧(NgWordPanel の .ngrow)と揃えている
 * (同じ「レベル色 = 枠の色」で対応が分かるように)。
 * 行にマウスを乗せると hover イベントで添字を通知し、画像側の該当枠を強調させる。
 * 見出しの「枠を表示」で画像上の枠の表示/非表示を切り替える(v-model:showBoxes)。
 */
import type { OcrNgHit } from '@/types/api'
import { openExternal } from '@/utils/links'
import { levelClass } from '@/utils/ocrNg'

defineProps<{
  hits: OcrNgHit[]
  activeIndex: number | null
  showBoxes: boolean
}>()

const emit = defineEmits<{
  (e: 'hover', index: number | null): void
  (e: 'update:showBoxes', value: boolean): void
}>()
</script>

<template>
  <div class="ocr-ng-list">
    <div class="ocr-ng-list-head">
      <span>画像内のNGワード {{ hits.length }}件</span>
      <label
        class="ocr-box-toggle"
        title="画像上のNGワードの枠を表示します。非表示でも、一覧の行にマウスを乗せている間はその語の枠を表示します"
      >
        <input
          type="checkbox"
          :checked="showBoxes"
          @change="emit('update:showBoxes', ($event.target as HTMLInputElement).checked)"
        />
        枠を表示
      </label>
    </div>
    <div class="ocr-ng-list-body">
      <div
        v-for="(h, i) in hits"
        :key="`${h.word}-${i}`"
        class="ngrow ocr-ngrow"
        :class="[levelClass(h.level), { active: activeIndex === i }]"
        @mouseenter="emit('hover', i)"
        @mouseleave="emit('hover', null)"
      >
        <div class="ngrow-word">{{ h.word }}</div>
        <div class="ngrow-reason">{{ h.reason }}</div>
        <div class="ngrow-side">
          <a
            v-if="h.link"
            class="ngrow-link"
            title="参考リンクを開く"
            @click.stop="openExternal(h.link)"
          >
            <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
          </a>
          <span class="ngrow-level">Lv.{{ h.level }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
